<template>
  <card shadow>
    <template v-slot:header>
      <div class="row align-items-center">
        <div class="col">
          <h3 class="mb-0">Timesheets</h3>
          <p class="text-muted text-sm mb-0">
            Timesheet records used for this tip split
          </p>
        </div>
        <div class="col-auto d-flex align-items-center">
          <input
            type="text"
            v-model="searchQuery"
            class="form-control form-control-sm mr-3"
            placeholder="Search by name, staff ID, job, location..."
            style="width: 280px"
          />
          <button class="btn btn-sm btn-success" @click="exportData">
            <i class="fas fa-download mr-1"></i>Export
          </button>
        </div>
      </div>
    </template>

    <div class="table-responsive">
      <table class="table align-items-center table-flush">
        <thead class="thead-light">
          <tr>
            <sortable-header
              sort-key="name"
              :sort-state="sortState"
              @sort="handleSort"
            >
              Employee
            </sortable-header>
            <sortable-header
              sort-key="job"
              :sort-state="sortState"
              @sort="handleSort"
            >
              Job
            </sortable-header>
            <sortable-header
              sort-key="locationName"
              :sort-state="sortState"
              @sort="handleSort"
            >
              Location
            </sortable-header>
            <sortable-header
              sort-key="start"
              :sort-state="sortState"
              @sort="handleSort"
            >
              Shift Start
            </sortable-header>
            <sortable-header
              sort-key="end"
              :sort-state="sortState"
              @sort="handleSort"
            >
              Shift End
            </sortable-header>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(ts, idx) in paginatedData" :key="idx">
            <td class="align-top">
              <strong>{{ ts.name || ts.staffId }}</strong>
              <small class="text-muted d-block" style="line-height: 1.2">
                Staff ID:
                <copy-text :text="ts.staffId" label="Staff ID" inline>
                  <span class="text-monospace">{{ ts.staffId }}</span>
                </copy-text>
              </small>
            </td>
            <td>{{ ts.job }}</td>
            <td>{{ ts.locationName || ts.locationId }}</td>
            <td>{{ formatShiftTime(ts.start) }}</td>
            <td>{{ formatShiftTime(ts.end) }}</td>
          </tr>
          <tr v-if="paginatedData.length === 0">
            <td colspan="5" class="text-center text-muted py-4">
              {{ searchQuery ? 'No timesheets match your search' : 'No timesheet data available' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <table-pagination
      v-model:current-page="currentPage"
      :total-items="filteredData.length"
      :per-page="perPage"
      item-label="timesheets"
    />
  </card>
</template>

<script>
import CopyText from "@/components/CopyText";
import TablePagination from "@/components/TablePagination";
import SortableHeader from "@/components/SortableHeader";
import { formatShiftTime, exportToCSV } from "@/utils/formatters";

export default {
  name: "TimesheetsTab",
  components: {
    CopyText,
    TablePagination,
    SortableHeader,
  },
  props: {
    timesheets: {
      type: Array,
      required: true,
    },
    staffLookup: {
      type: Object,
      default: () => ({}),
    },
    locationLookup: {
      type: Object,
      default: () => ({}),
    },
    reportId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      searchQuery: "",
      currentPage: 1,
      perPage: 15,
      sortState: { key: "start", order: "desc" },
    };
  },
  computed: {
    enrichedTimesheets() {
      return this.timesheets.map((ts) => ({
        ...ts,
        name: this.staffLookup[ts.staffId] || "",
        locationName: this.locationLookup[ts.locationId] || "",
      }));
    },
    filteredData() {
      let result = [...this.enrichedTimesheets];

      if (this.searchQuery) {
        const search = this.searchQuery.toLowerCase();
        result = result.filter(
          (ts) =>
            ts.name?.toLowerCase().includes(search) ||
            ts.staffId?.toLowerCase().includes(search) ||
            ts.job?.toLowerCase().includes(search) ||
            ts.locationName?.toLowerCase().includes(search) ||
            ts.locationId?.toLowerCase().includes(search)
        );
      }

      result.sort((a, b) => {
        let aVal = a[this.sortState.key];
        let bVal = b[this.sortState.key];

        if (aVal < bVal) return this.sortState.order === "asc" ? -1 : 1;
        if (aVal > bVal) return this.sortState.order === "asc" ? 1 : -1;
        return 0;
      });

      return result;
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredData.slice(start, start + this.perPage);
    },
  },
  methods: {
    formatShiftTime,
    handleSort(key) {
      if (this.sortState.key === key) {
        this.sortState.order = this.sortState.order === "asc" ? "desc" : "asc";
      } else {
        this.sortState.key = key;
        this.sortState.order = "asc";
      }
    },
    exportData() {
      exportToCSV({
        data: this.filteredData,
        columns: [
          { header: "Employee Name", value: (ts) => ts.name || ts.staffId },
          { header: "Staff ID", value: "staffId" },
          { header: "Job", value: "job" },
          { header: "Location", value: (ts) => ts.locationName || ts.locationId },
          { header: "Shift Start", value: (ts) => ts.start || "" },
          { header: "Shift End", value: (ts) => ts.end || "" },
        ],
        filename: `timesheets-${this.reportId}.csv`,
      });
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.text-monospace {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
    "Courier New", monospace;
  font-size: 0.8rem;
}
</style>
